import { useState } from "react";
import RightSideSheet, {
  FILTER_SHEET_WIDTHS,
} from "@/components/ui/others/RightSideSheet";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  setIsPeerTransfer,
  setCurrentLeadId,
} from "@/features/leads/stores/leadFunctionalSlice";
import { Button } from "@/components/ui/button";
import SearchInput from "@/components/ui/searchInput";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import usePeerEligibility from "@/features/leads/api/usePeerEligibility";
import PeerPickerList from "./PeerPickerList";

/**
 * "Transfer to peers" drawer. Opened (via the assign "+" in peer mode) for
 * roles that hold `assignation_management.outbound_peer` but NOT full outbound
 * assign. No role picking — it lists the eligible SAME-ROLE peers (server-side
 * branch + custom-field filtered) and hands the lead/deal off to the chosen
 * one, revoking the current user (handover). All the usual assignment rules
 * still apply; the server is authoritative.
 *
 * A role with BOTH toggles gets the same peers inside the Assign User drawer
 * instead (its "your peers" row), so the full picker and this sheet never
 * disagree on who can receive a hand-over.
 */
export default function PeerTransferSheet() {
  const isPeerTransfer = useAppSelector(
    (s) => s.leadFunctional.isPeerTransfer,
  );
  const dispatch = useAppDispatch();
  const { lead } = useLeadForm();
  const { currentChildId } = useChildForm();

  const isChildActive = !!currentChildId;
  const childId =
    isChildActive && currentChildId != null ? Number(currentChildId) : null;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPeerId, setSelectedPeerId] = useState<number | null>(null);

  const { peers, isLoading, errorMessage } = usePeerEligibility(
    lead?.id ?? null,
    childId,
    isPeerTransfer,
  );
  const { peerTransfer } = useLeadMutations();

  const selectedPeer = peers.find((p) => p.id === selectedPeerId) ?? null;

  const handleClose = () => {
    dispatch(setIsPeerTransfer(false));
    setSearchTerm("");
    setSelectedPeerId(null);
  };

  const handleTransfer = () => {
    if (!lead?.id || selectedPeerId == null) return;
    peerTransfer.mutate(
      { leadId: lead.id, toUserId: selectedPeerId, childId },
      {
        onSuccess: () => {
          handleClose();
          // The actor handed it off and lost access — close the detail drawer
          // (redux-opened path). Board invalidation drops it from their list.
          dispatch(setCurrentLeadId(null));
        },
      },
    );
  };

  return (
    <RightSideSheet
      widths={FILTER_SHEET_WIDTHS}
      open={isPeerTransfer}
      handleClose={handleClose}
      title={isChildActive ? "Transfer deal to a peer" : "Transfer lead to a peer"}
      description="Hand this off to another user on your role. They must pass the same branch + custom-field rules; you lose access once transferred."
      actions={
        <div className="flex items-center justify-end gap-2">
          <Button variant="cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleTransfer}
            disabled={selectedPeerId == null || peerTransfer.isPending}
          >
            {peerTransfer.isPending
              ? "Transferring…"
              : selectedPeer
                ? `Hand over to ${selectedPeer.name ?? "peer"}`
                : "Hand over"}
          </Button>
        </div>
      }
    >
      <div className="px-5 py-2 flex flex-col gap-4">
        <div className="sticky top-2 z-10 bg-white">
          <SearchInput
            placeholder="Search peers"
            isKbd={false}
            className="flex-1"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
          />
        </div>

        <PeerPickerList
          peers={peers}
          isLoading={isLoading}
          error={errorMessage}
          selectedPeerId={selectedPeerId}
          onSelect={setSelectedPeerId}
          searchTerm={searchTerm}
        />
      </div>
    </RightSideSheet>
  );
}
